import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Check, Volume2, Loader2 } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const GEMINI_VOICE_OPTIONS = [
  { id: "Aoede", name: "Aoede", desc: "douce et chaleureuse" },
  { id: "Kore", name: "Kore", desc: "claire et posée" },
  { id: "Leda", name: "Leda", desc: "jeune et énergique" },
  { id: "Callirrhoe", name: "Callirrhoe", desc: "calme et naturelle" },
  { id: "Autonoe", name: "Autonoe", desc: "lumineuse" },
  { id: "Despina", name: "Despina", desc: "fluide" },
  { id: "Vindemiatrix", name: "Vindemiatrix", desc: "chaleureuse" },
];

const PRESETS = [
  { name: "Cyan", hue: 185 },
  { name: "Or", hue: 43 },
  { name: "Émeraude", hue: 144 },
  { name: "Cramoisi", hue: 345 },
  { name: "Violet", hue: 270 },
  { name: "Azur", hue: 210 },
];

export function SettingsDrawer({ open, onClose }) {
  const { user, updateSettings, applyHue } = useAuth();
  const [assistantName, setAssistantName] = useState("");
  const [userName, setUserName] = useState("");
  const [hue, setHue] = useState(185);
  const [wakeEnabled, setWakeEnabled] = useState(false);
  const [wakeWord, setWakeWord] = useState("Sara");
  const [silenceMs, setSilenceMs] = useState(1100);
  const [whisperDefault, setWhisperDefault] = useState(false);
  const [hesitations, setHesitations] = useState(true);
  const [prudentMode, setPrudentMode] = useState(true);
  const [geminiVoice, setGeminiVoice] = useState("Aoede");
  const [ttsRate, setTtsRate] = useState(1.0);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && open) {
      setAssistantName(user.assistant_name || "Sara Connor");
      setUserName(user.name || "");
      setHue(user.hue ?? 185);
      setWakeEnabled(user.wake_word_enabled ?? false);
      setWakeWord(user.wake_word || "Sara");
      setSilenceMs(user.silence_ms ?? 1100);
      setWhisperDefault(user.whisper_default ?? false);
      setHesitations(user.hesitations ?? true);
      setPrudentMode(user.prudent_mode ?? true);
      setGeminiVoice(user.gemini_voice || "Aoede");
      setTtsRate(typeof user.tts_rate === "number" ? user.tts_rate : 1.0);
    }
  }, [user, open]);

  const onHue = (v) => {
    setHue(v);
    applyHue(v);
  };

  const handleOpenChange = (o) => {
    if (!o) {
      applyHue(user?.hue ?? 185);
      onClose();
    }
  };

  const previewVoice = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/tts/generate", {
        text: "Bonjour, je suis Sara. Voici un aperçu de ma voix.",
        gemini_voice: geminiVoice,
      });
      const audio = new Audio(data.audio);
      audio.playbackRate = Math.max(0.7, Math.min(1.3, ttsRate || 1));
      audio.onerror = () => toast.error("Aperçu indisponible");
      await audio.play();
    } catch (e) {
      toast.error("Aperçu indisponible");
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings({
        assistant_name: assistantName,
        name: userName,
        hue,
        wake_word_enabled: wakeEnabled,
        wake_word: wakeWord,
        silence_ms: silenceMs,
        whisper_default: whisperDefault,
        hesitations,
        prudent_mode: prudentMode,
        gemini_voice: geminiVoice,
        tts_rate: ttsRate,
      });
      localStorage.setItem("sara_tts_rate", String(ttsRate));
      toast.success("Configuration enregistrée");
      onClose();
    } catch (e) {
      toast.error("Échec de l'enregistrement de la configuration");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        data-testid="settings-drawer"
        side="right"
        className="w-full sm:max-w-md bg-[var(--panel-solid)] border-l border-[var(--border-hud)] text-white flex flex-col p-0 gap-0"
      >
        <SheetHeader className="shrink-0 px-6 pt-6 pb-3">
          <SheetTitle className="font-display tracking-[0.15em] accent-text text-glow">CONFIGURATION SYSTÈME</SheetTitle>
          <SheetDescription className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e]">
            Personnalisez Sara Connor : nom, voix, couleur d'accent et mot d'activation.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
          <Accordion type="multiple" defaultValue={["identite", "voix"]} className="w-full">
            <SettingsSection value="identite" label="IDENTITÉ & APPARENCE">
              <Group label="NOM DE L'ASSISTANTE">
            <input
              data-testid="assistant-name-input"
              value={assistantName}
              onChange={(e) => setAssistantName(e.target.value)}
              className="hud-input"
            />
          </Group>

          <Group label="NOM DE L'OPÉRATEUR">
            <input
              data-testid="user-name-input"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              className="hud-input"
            />
          </Group>

          <Group label={`TEINTE D'ACCENT // ${hue}°`}>
            <input
              data-testid="accent-hue-slider"
              type="range"
              min="0"
              max="360"
              value={hue}
              onChange={(e) => onHue(Number(e.target.value))}
              className="w-full accent-[var(--accent)]"
              style={{
                background: "linear-gradient(90deg,hsl(0,100%,55%),hsl(60,100%,55%),hsl(120,100%,55%),hsl(180,100%,55%),hsl(240,100%,55%),hsl(300,100%,55%),hsl(360,100%,55%))",
                height: 6,
                borderRadius: 20,
                appearance: "none",
              }}
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.name}
                  data-testid="accent-preset-button"
                  onClick={() => onHue(p.hue)}
                  title={p.name}
                  className="h-8 w-8 rounded-full border-2 flex items-center justify-center transition-transform hover:scale-110"
                  style={{
                    background: `hsl(${p.hue},100%,55%)`,
                    borderColor: hue === p.hue ? "#fff" : "transparent",
                    boxShadow: `0 0 12px hsla(${p.hue},100%,55%,0.6)`,
                  }}
                >
                  {hue === p.hue && <Check className="h-4 w-4 text-black" />}
                </button>
              ))}
            </div>
          </Group>
            </SettingsSection>

            <SettingsSection value="voix" label="VOIX & PAROLE">
          <Group label="VOIX DE SARA (GEMINI · GRATUITE)">
            <div data-testid="gemini-voice-grid" className="grid grid-cols-2 gap-2">
              {GEMINI_VOICE_OPTIONS.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  data-testid={`gemini-voice-${v.id}`}
                  onClick={() => setGeminiVoice(v.id)}
                  className={`text-left px-3 py-2 border clip-corner transition-colors ${
                    geminiVoice === v.id
                      ? "border-[var(--accent)] bg-[var(--accent-a10)]"
                      : "border-[var(--border-hud)] bg-[rgba(2,8,18,0.8)] hover:bg-[var(--accent-a10)]"
                  }`}
                >
                  <span className="block text-sm text-[#c8d6e5]">{v.name}</span>
                  <span className="block font-mono-hud text-[10px] text-[#8b949e]">{v.desc}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="gemini-voice-preview"
              onClick={previewVoice}
              disabled={previewing}
              className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 border border-[var(--border-hud)] clip-corner bg-[rgba(2,8,18,0.8)] hover:bg-[var(--accent-a10)] transition-colors disabled:opacity-50"
            >
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4 accent-text" />}
              <span className="text-sm text-[#c8d6e5]">{previewing ? "Génération…" : "Écouter un aperçu"}</span>
            </button>
            <p className="mt-1 font-mono-hud text-[10px] text-[#8b949e] leading-snug">
              Voix gratuite via votre clé Gemini — aucun crédit Emergent. L'aperçu utilise aussi le débit choisi ci-dessous.
            </p>
          </Group>

          <Group label="DÉBIT DE PAROLE">
            <div className="grid grid-cols-3 gap-2" data-testid="tts-rate-group">
              {[{ v: 0.85, l: "Lent" }, { v: 1.0, l: "Normal" }, { v: 1.15, l: "Rapide" }].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`tts-rate-${o.l.toLowerCase()}`}
                  onClick={() => setTtsRate(o.v)}
                  className={`px-3 py-2 border clip-corner text-sm transition-colors ${
                    Math.abs(ttsRate - o.v) < 0.01
                      ? "border-[var(--accent)] bg-[var(--accent-a10)] text-white"
                      : "border-[var(--border-hud)] bg-[rgba(2,8,18,0.8)] text-[#c8d6e5] hover:bg-[var(--accent-a10)]"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </Group>

          <Group label="TON NATUREL">
            <button
              type="button"
              data-testid="hesitations-toggle"
              role="switch"
              aria-checked={hesitations}
              onClick={() => setHesitations((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2.5 border border-[var(--border-hud)] clip-corner bg-[rgba(2,8,18,0.8)] hover:bg-[var(--accent-a10)] transition-colors"
            >
              <span className="text-sm text-[#c8d6e5] text-left">
                {hesitations ? "Hésitations naturelles activées" : "Diction directe (sans hésitations)"}
              </span>
              <span className={`relative h-5 w-9 rounded-full transition-colors shrink-0 ${hesitations ? "bg-[var(--accent)]" : "bg-[#2a3a4a]"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${hesitations ? "left-[18px]" : "left-0.5"}`} />
              </span>
            </button>
            <p className="mt-1 font-mono-hud text-[10px] text-[#8b949e] leading-snug">
              Sara marque un court temps de réflexion et glisse parfois un « euh… », « hmm… » avant de répondre, pour une voix plus humaine. Seule la voix est concernée — le texte affiché reste net.
            </p>
          </Group>
            </SettingsSection>

            <SettingsSection value="comportement" label="COMPORTEMENT & ACTIONS">
          <Group label="CONTRÔLE DES ACTIONS">
            <button
              type="button"
              data-testid="prudent-mode-toggle"
              role="switch"
              aria-checked={prudentMode}
              onClick={() => setPrudentMode((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2.5 border border-[var(--border-hud)] clip-corner bg-[rgba(2,8,18,0.8)] hover:bg-[var(--accent-a10)] transition-colors"
            >
              <span className="text-sm text-[#c8d6e5] text-left">
                {prudentMode ? "Mode prudent — demander avant d'agir" : "Mode direct — Sara agit immédiatement"}
              </span>
              <span className={`relative h-5 w-9 rounded-full transition-colors shrink-0 ${prudentMode ? "bg-[var(--accent)]" : "bg-[#2a3a4a]"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${prudentMode ? "left-[18px]" : "left-0.5"}`} />
              </span>
            </button>
            <p className="mt-1 font-mono-hud text-[10px] text-[#8b949e] leading-snug">
              En mode prudent, Sara te demande confirmation avant de générer du code, une image, de chercher sur le web ou de naviguer — tu gardes le contrôle. Désactive-le pour qu'elle exécute directement et aille plus vite.
            </p>
          </Group>
            </SettingsSection>


            <SettingsSection value="saisie" label="SAISIE VOCALE & ÉCOUTE">
          <Group label="SAISIE VOCALE">
            <label className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] block mb-1">
              DÉLAI DE SILENCE AVANT ENVOI // {(silenceMs / 1000).toFixed(1)}s
            </label>
            <input
              data-testid="silence-ms-slider"
              type="range"
              min="300"
              max="2000"
              step="100"
              value={silenceMs}
              onChange={(e) => setSilenceMs(Number(e.target.value))}
              className="w-full accent-[var(--accent)]"
              style={{ height: 6, borderRadius: 20 }}
            />
            <div className="flex justify-between font-mono-hud text-[9px] text-[#6b7a8d] mt-1">
              <span>0,3s · instantané</span>
              <span>2,0s · posé</span>
            </div>
            <p className="mt-1 font-mono-hud text-[10px] text-[#8b949e] leading-snug">
              Temps de silence après ta phrase avant que Sara ne l'envoie automatiquement. Augmente-le si tu parles lentement.
            </p>

            <button
              type="button"
              data-testid="whisper-default-toggle"
              onClick={() => setWhisperDefault((v) => !v)}
              className="mt-4 w-full flex items-center justify-between px-3 py-2.5 border border-[var(--border-hud)] clip-corner bg-[rgba(2,8,18,0.8)] hover:bg-[var(--accent-a10)] transition-colors"
            >
              <span className="text-sm text-[#c8d6e5] text-left">
                {whisperDefault ? "Whisper par défaut — appui-pour-parler (plus fiable)" : "Reconnaissance du navigateur (mains libres continu)"}
              </span>
              <span className={`relative h-5 w-9 rounded-full transition-colors shrink-0 ${whisperDefault ? "bg-[var(--accent)]" : "bg-[#2a3a4a]"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${whisperDefault ? "left-[18px]" : "left-0.5"}`} />
              </span>
            </button>
            <p className="mt-1 font-mono-hud text-[10px] text-[#8b949e] leading-snug">
              Whisper transcrit après ton appui (démarrer / arrêter) — plus précis et sans phrases dupliquées, idéal sur mobile.
            </p>
          </Group>

          <Group label="MOT D'ACTIVATION">
            <button
              type="button"
              data-testid="wake-word-toggle"
              onClick={() => setWakeEnabled((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2.5 border border-[var(--border-hud)] clip-corner bg-[rgba(2,8,18,0.8)] hover:bg-[var(--accent-a10)] transition-colors"
            >
              <span className="text-sm text-[#c8d6e5]">
                {wakeEnabled ? "Activé — Sara n'écoute qu'après le mot" : "Désactivé — Sara écoute en continu"}
              </span>
              <span
                className={`relative h-5 w-9 rounded-full transition-colors ${
                  wakeEnabled ? "bg-[var(--accent)]" : "bg-[#2a3a4a]"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
                    wakeEnabled ? "left-[18px]" : "left-0.5"
                  }`}
                />
              </span>
            </button>
            {wakeEnabled && (
              <input
                data-testid="wake-word-input"
                value={wakeWord}
                onChange={(e) => setWakeWord(e.target.value)}
                placeholder="Sara"
                className="hud-input mt-2"
              />
            )}
            <p className="mt-1 font-mono-hud text-[10px] text-[#8b949e] leading-snug">
              Dites par ex. « {wakeWord || "Sara"}, quelle heure est-il ? ». Fonctionne uniquement quand l'onglet est ouvert.
            </p>
          </Group>
            </SettingsSection>
          </Accordion>
        </div>

        <div className="shrink-0 border-t border-[var(--border-hud)] bg-[var(--panel-solid)] px-6 py-4">
          <button
            data-testid="settings-save-button"
            onClick={save}
            disabled={saving}
            className="hud-btn clip-corner w-full py-3.5 text-sm font-bold disabled:opacity-50"
          >
            {saving ? "ENREGISTREMENT..." : "ENREGISTRER LA CONFIGURATION"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SettingsSection({ value, label, children }) {
  return (
    <AccordionItem value={value} className="border-b border-[var(--border-hud)]">
      <AccordionTrigger
        data-testid={`settings-section-${value}`}
        className="font-mono-hud text-[11px] tracking-[0.25em] accent-text hover:no-underline py-4"
      >
        {label}
      </AccordionTrigger>
      <AccordionContent className="space-y-6 pt-1">{children}</AccordionContent>
    </AccordionItem>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <label className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] block mb-2">{label}</label>
      {children}
    </div>
  );
}

